import type { Match, Team } from "@/types/match";
import { leaguesById } from "@/data/leagues";
import { colorsFromString } from "@/utils/color";

const team = (id: string, name: string, shortName: string): Team => {
  const { primary, secondary } = colorsFromString(id);
  return { id, name, shortName, crestUrl: null, primaryColor: primary, secondaryColor: secondary };
};

const teams = {
  arsenal: team("arsenal", "Arsenal", "ARS"),
  chelsea: team("chelsea", "Chelsea", "CHE"),
  mancity: team("mancity", "Manchester City", "MCI"),
  liverpool: team("liverpool", "Liverpool", "LIV"),
  realmadrid: team("realmadrid", "Real Madrid", "RMA"),
  barcelona: team("barcelona", "Barcelona", "BAR"),
  atleticomadrid: team("atleticomadrid", "Atlético Madrid", "ATM"),
  sevilla: team("sevilla", "Sevilla", "SEV"),
  bayern: team("bayern", "Bayern Munich", "BAY"),
  dortmund: team("dortmund", "Borussia Dortmund", "BVB"),
  juventus: team("juventus", "Juventus", "JUV"),
  intermilan: team("intermilan", "Inter Milan", "INT"),
  acmilan: team("acmilan", "AC Milan", "MIL"),
  psg: team("psg", "Paris Saint-Germain", "PSG"),
  marseille: team("marseille", "Marseille", "OM"),
  napoli: team("napoli", "Napoli", "NAP"),
  alhilal: team("alhilal", "Al Hilal", "HIL"),
  alnassr: team("alnassr", "Al Nassr", "NAS"),
  interamiami: team("interamiami", "Inter Miami", "MIA"),
  flamengo: team("flamengo", "Flamengo", "FLA"),
  bocajuniors: team("bocajuniors", "Boca Juniors", "BOC"),
  wydad: team("wydad", "Wydad AC", "WYD"),
  alahly: team("alahly", "Al Ahly", "AHL"),
  england: team("england", "England", "ENG"),
  france: team("france", "France", "FRA"),
  brazil: team("brazil", "Brazil", "BRA"),
  argentina: team("argentina", "Argentina", "ARG"),
  nigeria: team("nigeria", "Nigeria", "NGA"),
  senegal: team("senegal", "Senegal", "SEN"),
  morocco: team("morocco", "Morocco", "MAR"),
  japan: team("japan", "Japan", "JPN"),
};

// Demo HLS stream — Apple's public bipbop test asset, used here purely so
// the player logic (Hls.js wiring, cleanup, fullscreen, quality switching)
// is fully exercisable at runtime. Swap for real signed stream URLs from
// your media backend in production — see streamResolver.ts.
const demoStream = (): Match["streams"] => [
  {
    quality: "auto",
    type: "hls",
    url: "https://iframe.rumsport8.live/stream.m3u8?is_vip=false&token=4d82950535d98459fc35bce9181eecaedd7d2c2d&url=https%3A%2F%2Flivecdn-tc-livestream.rumsport10.live%2Fsla%2F1-6a6a1cf8000f680c3c906818-backup%2Findex.m3u8&verify=1787572800-Xqu4aC3uUWQm5z5s83faVm53uSnZd0jBGD9wZ75JU5k%3D",
  },
];

const now = Date.now();
const iso = (offsetMinutes: number) => new Date(now + offsetMinutes * 60_000).toISOString();
const league = (id: string) => {
  const found = leaguesById.get(id);
  if (!found) throw new Error(`Unknown league id: ${id}`);
  return found;
};

const emptyMatchFields = {
  streams: [] as Match["streams"],
  events: [] as Match["events"],
  commentary: [] as Match["commentary"],
  stats: null,
  lineups: null,
};

export const mockMatches: Match[] = [
  {
    id: "m1",
    league: league("epl"),
    homeTeam: teams.arsenal,
    awayTeam: teams.chelsea,
    status: "LIVE",
    kickoff: iso(-63),
    minute: 63,
    homeScore: 2,
    awayScore: 1,
    venue: "Emirates Stadium, London",
    streams: demoStream(),
    events: [
      { id: "e1", minute: 12, type: "GOAL", teamId: "arsenal", playerName: "B. Saka" },
      { id: "e2", minute: 35, type: "GOAL", teamId: "chelsea", playerName: "C. Palmer" },
      { id: "e3", minute: 58, type: "GOAL", teamId: "arsenal", playerName: "M. Ødegaard" },
      { id: "e4", minute: 61, type: "YELLOW_CARD", teamId: "chelsea", playerName: "M. Caicedo" },
    ],
    commentary: [
      { id: "c1", minute: 63, text: "Arsenal working the ball patiently in midfield, content to hold the lead.", isKeyMoment: false },
      { id: "c2", minute: 61, text: "Caicedo goes into the book for a late challenge on Rice.", isKeyMoment: true },
      { id: "c3", minute: 58, text: "GOAL! Ødegaard curls one into the far corner from the edge of the box!", isKeyMoment: true },
    ],
    stats: {
      possession: [57, 43],
      shots: [14, 9],
      shotsOnTarget: [6, 4],
      corners: [7, 3],
      fouls: [8, 11],
      offsides: [2, 1],
    },
    lineups: {
      home: {
        formation: "4-3-3",
        starters: [
          { number: 1, name: "D. Raya", position: "GK" },
          { number: 4, name: "B. White", position: "RB" },
          { number: 6, name: "G. Magalhães", position: "CB", isCaptain: true },
          { number: 5, name: "W. Saliba", position: "CB" },
          { number: 3, name: "O. Zinchenko", position: "LB" },
          { number: 41, name: "D. Rice", position: "CM" },
          { number: 8, name: "M. Ødegaard", position: "CM" },
          { number: 29, name: "K. Havertz", position: "CM" },
          { number: 7, name: "B. Saka", position: "RW" },
          { number: 9, name: "G. Jesus", position: "ST" },
          { number: 11, name: "G. Martinelli", position: "LW" },
        ],
        substitutes: [
          { number: 22, name: "N. Vieira", position: "GK" },
          { number: 15, name: "J. Timber", position: "DF" },
          { number: 18, name: "T. Tomiyasu", position: "DF" },
        ],
      },
      away: {
        formation: "4-2-3-1",
        starters: [
          { number: 1, name: "R. Sánchez", position: "GK" },
          { number: 24, name: "R. James", position: "RB", isCaptain: true },
          { number: 6, name: "T. Adarabioyo", position: "CB" },
          { number: 26, name: "W. Fofana", position: "CB" },
          { number: 21, name: "M. Cucurella", position: "LB" },
          { number: 25, name: "M. Caicedo", position: "CM" },
          { number: 5, name: "E. Fernández", position: "CM" },
          { number: 20, name: "C. Palmer", position: "AM" },
          { number: 17, name: "M. Mudryk", position: "RW" },
          { number: 15, name: "N. Jackson", position: "ST" },
          { number: 11, name: "P. Neto", position: "LW" },
        ],
        substitutes: [
          { number: 13, name: "F. Jörgensen", position: "GK" },
          { number: 3, name: "M. Disasi", position: "DF" },
        ],
      },
    },
  },
  {
    id: "m2",
    league: league("ucl"),
    homeTeam: teams.realmadrid,
    awayTeam: teams.bayern,
    status: "LIVE",
    kickoff: iso(-27),
    minute: 27,
    homeScore: 0,
    awayScore: 0,
    venue: "Santiago Bernabéu, Madrid",
    streams: demoStream(),
    events: [{ id: "e5", minute: 19, type: "YELLOW_CARD", teamId: "bayern", playerName: "J. Kimmich" }],
    commentary: [
      { id: "c4", minute: 27, text: "Cagey opening period, both sides feeling each other out.", isKeyMoment: false },
      { id: "c5", minute: 19, text: "Kimmich booked for scything down Vinícius on the counter.", isKeyMoment: true },
    ],
    stats: { possession: [48, 52], shots: [3, 5], shotsOnTarget: [1, 2], corners: [2, 1], fouls: [5, 6], offsides: [0, 1] },
    lineups: null,
  },
  {
    id: "m3",
    league: league("seriea"),
    homeTeam: teams.juventus,
    awayTeam: teams.intermilan,
    status: "HALFTIME",
    kickoff: iso(-48),
    minute: 45,
    homeScore: 1,
    awayScore: 1,
    venue: "Allianz Stadium, Turin",
    streams: demoStream(),
    events: [
      { id: "e6", minute: 22, type: "GOAL", teamId: "juventus", playerName: "D. Vlahović" },
      { id: "e7", minute: 40, type: "GOAL", teamId: "intermilan", playerName: "L. Martínez" },
    ],
    commentary: [{ id: "c6", minute: 45, text: "Referee blows for half-time with the sides level.", isKeyMoment: false }],
    stats: { possession: [51, 49], shots: [7, 8], shotsOnTarget: [3, 4], corners: [4, 5], fouls: [6, 7], offsides: [1, 0] },
    lineups: null,
  },
  {
    id: "m4",
    league: league("laliga"),
    homeTeam: teams.barcelona,
    awayTeam: teams.napoli,
    status: "SCHEDULED",
    kickoff: iso(95),
    minute: null,
    homeScore: 0,
    awayScore: 0,
    venue: "Estadi Olímpic Lluís Companys, Barcelona",
    ...emptyMatchFields,
  },
  {
    id: "m5",
    league: league("bundesliga"),
    homeTeam: teams.dortmund,
    awayTeam: teams.bayern,
    status: "SCHEDULED",
    kickoff: iso(220),
    minute: null,
    homeScore: 0,
    awayScore: 0,
    venue: "Signal Iduna Park, Dortmund",
    ...emptyMatchFields,
  },
  {
    id: "m6",
    league: league("ucl"),
    homeTeam: teams.psg,
    awayTeam: teams.mancity,
    status: "SCHEDULED",
    kickoff: iso(340),
    minute: null,
    homeScore: 0,
    awayScore: 0,
    venue: "Parc des Princes, Paris",
    ...emptyMatchFields,
  },
  {
    id: "m7",
    league: league("epl"),
    homeTeam: teams.liverpool,
    awayTeam: teams.mancity,
    status: "FULLTIME",
    kickoff: iso(-260),
    minute: 90,
    homeScore: 3,
    awayScore: 2,
    venue: "Anfield, Liverpool",
    streams: [],
    events: [],
    commentary: [],
    stats: { possession: [55, 45], shots: [16, 12], shotsOnTarget: [8, 6], corners: [6, 4], fouls: [9, 10], offsides: [2, 3] },
    lineups: null,
  },
  {
    id: "m8",
    league: league("laliga"),
    homeTeam: teams.realmadrid,
    awayTeam: teams.barcelona,
    status: "POSTPONED",
    kickoff: iso(-120),
    minute: null,
    homeScore: 0,
    awayScore: 0,
    venue: "Santiago Bernabéu, Madrid",
    ...emptyMatchFields,
  },

  // ── Additional competitions ─────────────────────────────────────────
  {
    id: "m9",
    league: league("copadelrey"),
    homeTeam: teams.atleticomadrid,
    awayTeam: teams.sevilla,
    status: "LIVE",
    kickoff: iso(-71),
    minute: 71,
    homeScore: 2,
    awayScore: 0,
    venue: "Cívitas Metropolitano, Madrid",
    streams: demoStream(),
    events: [
      { id: "e8", minute: 30, type: "GOAL", teamId: "atleticomadrid", playerName: "A. Griezmann" },
      { id: "e9", minute: 66, type: "GOAL", teamId: "atleticomadrid", playerName: "Á. Morata" },
    ],
    commentary: [{ id: "c7", minute: 71, text: "Atlético cruising into the quarter-finals.", isKeyMoment: false }],
    stats: { possession: [60, 40], shots: [12, 5], shotsOnTarget: [7, 2], corners: [5, 2], fouls: [7, 9], offsides: [1, 0] },
    lineups: null,
  },
  {
    id: "m10",
    league: league("spanishsupercup"),
    homeTeam: teams.realmadrid,
    awayTeam: teams.atleticomadrid,
    status: "SCHEDULED",
    kickoff: iso(410),
    minute: null,
    homeScore: 0,
    awayScore: 0,
    venue: "King Fahd International Stadium, Riyadh",
    ...emptyMatchFields,
  },
  {
    id: "m11",
    league: league("facup"),
    homeTeam: teams.chelsea,
    awayTeam: teams.liverpool,
    status: "SCHEDULED",
    kickoff: iso(180),
    minute: null,
    homeScore: 0,
    awayScore: 0,
    venue: "Stamford Bridge, London",
    // Demo of the "watch on [broadcaster]" link — the realistic option for
    // real matches you don't hold streaming rights to. Shows up in
    // StreamFallback instead of an embeddable player. See
    // server/index.js's /api/streams/:fixtureId route for wiring this up
    // for real fixtures.
    externalWatchUrl: "https://www.bbc.co.uk/sport/football",
    ...emptyMatchFields,
  },
  {
    id: "m12",
    league: league("worldcup"),
    homeTeam: teams.brazil,
    awayTeam: teams.argentina,
    status: "LIVE",
    kickoff: iso(-12),
    minute: 12,
    homeScore: 0,
    awayScore: 0,
    venue: "Maracanã, Rio de Janeiro",
    streams: demoStream(),
    events: [],
    commentary: [{ id: "c8", minute: 10, text: "A raucous atmosphere as the Seleção push forward early.", isKeyMoment: false }],
    stats: { possession: [53, 47], shots: [2, 1], shotsOnTarget: [1, 0], corners: [1, 0], fouls: [2, 3], offsides: [0, 0] },
    lineups: null,
  },
  {
    id: "m13",
    league: league("friendlies"),
    homeTeam: teams.england,
    awayTeam: teams.france,
    status: "SCHEDULED",
    kickoff: iso(500),
    minute: null,
    homeScore: 0,
    awayScore: 0,
    venue: "Wembley Stadium, London",
    ...emptyMatchFields,
  },
  {
    id: "m14",
    league: league("nationsleague"),
    homeTeam: teams.france,
    awayTeam: teams.england,
    status: "SCHEDULED",
    kickoff: iso(600),
    minute: null,
    homeScore: 0,
    awayScore: 0,
    venue: "Stade de France, Paris",
    ...emptyMatchFields,
  },
  {
    id: "m15",
    league: league("afcon"),
    homeTeam: teams.nigeria,
    awayTeam: teams.senegal,
    status: "HALFTIME",
    kickoff: iso(-45),
    minute: 45,
    homeScore: 1,
    awayScore: 1,
    venue: "Stade Olympique, Abidjan",
    streams: demoStream(),
    events: [
      { id: "e10", minute: 15, type: "GOAL", teamId: "nigeria", playerName: "V. Osimhen" },
      { id: "e11", minute: 38, type: "GOAL", teamId: "senegal", playerName: "S. Mané" },
    ],
    commentary: [{ id: "c9", minute: 45, text: "Level at the break in a fiery AFCON semi-final.", isKeyMoment: true }],
    stats: { possession: [49, 51], shots: [6, 7], shotsOnTarget: [3, 3], corners: [3, 4], fouls: [8, 6], offsides: [1, 1] },
    lineups: null,
  },
  {
    id: "m16",
    league: league("saudiproleague"),
    homeTeam: teams.alhilal,
    awayTeam: teams.alnassr,
    status: "LIVE",
    kickoff: iso(-55),
    minute: 55,
    homeScore: 3,
    awayScore: 2,
    venue: "Kingdom Arena, Riyadh",
    streams: demoStream(),
    events: [
      { id: "e12", minute: 10, type: "GOAL", teamId: "alnassr", playerName: "Cristiano Ronaldo" },
      { id: "e13", minute: 25, type: "GOAL", teamId: "alhilal", playerName: "Neymar Jr." },
      { id: "e14", minute: 40, type: "GOAL", teamId: "alhilal", playerName: "A. Mitrović" },
      { id: "e15", minute: 50, type: "GOAL", teamId: "alnassr", playerName: "S. Mané" },
      { id: "e16", minute: 53, type: "GOAL", teamId: "alhilal", playerName: "Malcom" },
    ],
    commentary: [{ id: "c10", minute: 55, text: "End-to-end thriller in the Riyadh derby, five goals and counting.", isKeyMoment: true }],
    stats: { possession: [46, 54], shots: [9, 11], shotsOnTarget: [6, 7], corners: [4, 6], fouls: [5, 4], offsides: [2, 1] },
    lineups: null,
  },
  {
    id: "m17",
    league: league("clubworldcup"),
    homeTeam: teams.flamengo,
    awayTeam: teams.acmilan,
    status: "SCHEDULED",
    kickoff: iso(260),
    minute: null,
    homeScore: 0,
    awayScore: 0,
    venue: "Lusail Stadium, Lusail",
    ...emptyMatchFields,
  },
  {
    id: "m18",
    league: league("libertadores"),
    homeTeam: teams.bocajuniors,
    awayTeam: teams.flamengo,
    status: "SCHEDULED",
    kickoff: iso(310),
    minute: null,
    homeScore: 0,
    awayScore: 0,
    venue: "La Bombonera, Buenos Aires",
    ...emptyMatchFields,
  },
  {
    id: "m19",
    league: league("cafchampionsleague"),
    homeTeam: teams.wydad,
    awayTeam: teams.alahly,
    status: "SCHEDULED",
    kickoff: iso(150),
    minute: null,
    homeScore: 0,
    awayScore: 0,
    venue: "Stade Mohammed V, Casablanca",
    ...emptyMatchFields,
  },
  {
    id: "m20",
    league: league("mls"),
    homeTeam: teams.interamiami,
    awayTeam: teams.mancity,
    status: "SCHEDULED",
    kickoff: iso(700),
    minute: null,
    homeScore: 0,
    awayScore: 0,
    venue: "Chase Stadium, Fort Lauderdale",
    ...emptyMatchFields,
  },
  {
    id: "m21",
    league: league("euros"),
    homeTeam: teams.france,
    awayTeam: teams.morocco,
    status: "SCHEDULED",
    kickoff: iso(820),
    minute: null,
    homeScore: 0,
    awayScore: 0,
    venue: "Allianz Arena, Munich",
    ...emptyMatchFields,
  },
  {
    id: "m22",
    league: league("asiancup"),
    homeTeam: teams.japan,
    awayTeam: teams.morocco,
    status: "SCHEDULED",
    kickoff: iso(900),
    minute: null,
    homeScore: 0,
    awayScore: 0,
    venue: "Al Thumama Stadium, Doha",
    ...emptyMatchFields,
  },
];

export { allLeagues } from "@/data/leagues";
